<template>
  <div :class="{ alarm: true, 'is-small': size === 'small' }">
    <button
      :title="$t('알림')"
      type="button"
      class="alarm-button officehome-gnb-alarm"
      :class="{ new: notificationCount > 0, 'icon-button__selected': isNotificationListVisible }"
      @click="loadNotification"
    >
      <BellIcon v-if="size === 'small'" />
    </button>
    <div
      v-show="isNotificationListVisible"
      class="alarm-detail"
      ref="alarmDetail"
      tabindex="0"
      @focusout="closeDropdownMenu"
    >
      <div class="alarm-header">
        <p class="gt-float-left">{{ $t('알림') }}</p>
        <button type="button" title="모든 알림 읽음으로 표시" class="hw-icon" @click="readAllNotification">
          <i class="fal fa-tasks"></i>
        </button>
      </div>
      <div class="no-alarm" v-if="notificationList.length <= 0">{{ $t('알림이 없습니다') }}</div>
      <div class="alarm-list" v-else>
        <a
          href="#"
          @click.stop.prevent="
            readNotification(notificationInfo.read, notificationInfo.id, notificationInfo.endpoint.resource_path)
          "
          class="alarm-list-item"
          :class="notificationInfo.read === 2 ? 'already-read' : ''"
          v-for="notificationInfo in notificationList"
          :key="`noti-${notificationInfo.id}`"
        >
          <div class="gt-align-top gt-pr-16">
            <div class="thumb">
              <img :src="getPhotoUrl(notificationInfo)" alt="알람 아이콘" style="border-radius: 50%" />
            </div>
          </div>
          <div class="gt-align-top">
            <!--  kevin(이주오) 요청으로 업무관리만 예외를 둠 / 기획자 확인도 함께 받은 부분 임  -->
            <NotiTitleAndContent
              v-if="notificationInfo.solution === 'task'"
              :title="notificationInfo.message[0]"
              :content="notificationInfo.message[2]"
            />
            <NotiContent v-else :messages="notificationInfo.message" :alarmType="notificationInfo.alarm_type" />
            <p class="date">{{ notificationInfo.created_at }}</p>
          </div>
        </a>
      </div>
      <div ref="observed" style="height: 10px;"></div>
    </div>
  </div>
</template>

<script>
import BellIcon from './Icon/management-service/BellIcon.vue';
import NotiContent from './NotiContent.vue';
import NotiTitleAndContent from '@/components/NotiTitleAndContent.vue';

export default {
  name: 'Noti',
  components: {
    NotiTitleAndContent,
    BellIcon,
    NotiContent,
  },
  props: {
    size: {
      type: String,
      required: false,
      default: 'normal',
    },
    notificationCount: {
      type: Number,
      required: true,
    },
    notificationList: {
      type: Array,
      required: true,
    },
    isNotificationListVisible: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return { observer: null };
  },
  mounted() {
    this.observer = new IntersectionObserver(
      ([{ isIntersecting }]) => isIntersecting && this.$store.dispatch('notification/loadList'),
      { threshold: 0 },
    );
    this.observer.observe(this.$refs.observed);
  },
  beforeDestroy() {
    this.observer.disconnect();
  },
  methods: {
    loadNotification() {
      if (this.isNotificationListVisible) {
        this.setIsNotificationVisible(false);
      } else {
        this.setIsNotificationVisible(true);
        this.$emit('load-notification');
      }
    },
    setIsNotificationVisible(isVisible) {
      this.$emit('set-is-notification-visible', isVisible);
      isVisible && this.$nextTick(() => this.$refs.alarmDetail.focus());
    },
    closeDropdownMenu(e) {
      const isMenuButton = Boolean(e.relatedTarget?.closest('div.alarm'));
      if (!isMenuButton) this.$emit('set-is-notification-visible', false);
    },
    getLinkUrl(link) {
      let urlRegex = /^(https?):\/\/[^\s$.?#].[^\s]*$/;
      if (urlRegex.test(link)) {
        return link;
      }
      return process.env.VUE_APP_OFFICE_URL + link;
    },
    getPhotoUrl(noti) {
      const { creator_photourl: creatorPhotoUrl, solution, action } = noti;

      // 1 [예외 경우]
      // - 연차촉진 / 조직개편&인사발령
      // jerry 조직개편&인사발령
      if (solution === 'hr-admin' && (action === 'alp_notify' || action === 'transfer')) {
        return 'https://static.hiworks.com/office/assets/images/alarm/icon_notify_hr-admin-org.png';
      }

      // 2 [creator_photo_url 값이 있는 경우]
      const urlRegex = /^(https?):\/\/[^\s$.?#].[^\s]*$/;
      if (urlRegex.test(creatorPhotoUrl)) {
        return creatorPhotoUrl;
      }

      const imgTypeArr = [
        'bbs',
        'board',
        'mail',
        'ea',
        'shared',
        'schedule',
        'user',
        'insa',
        'hr',
        'hr-admin',
        'bill',
        'booking',
        'task',
        'drive',
        'office-rel',
      ];

      // 3 [solution 에 따라 사용되는 경우]
      if (!creatorPhotoUrl && imgTypeArr.includes(solution)) {
        return `https://static.hiworks.com/office/assets/images/alarm/icon_notify_${solution}.png`;
      }

      // 4 [사용자모양 보여주는 경우]
      return `https://static.hiworks.com/office/assets/images/alarm/icon_notify_user.png`;
    },
    readNotification(isRead, id, url) {
      this.$emit('read-notification', isRead === 2, id, this.getLinkUrl(url));
    },
    readAllNotification() {
      this.$emit('read-all-notification');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/base/variables.scss';

.alarm {
  @media (min-width: 900px) {
    position: relative;
  }
}

.alarm-button {
  width: $iconButtonSize;
  height: $iconButtonSize;
  background: url('https://static.hiworks.com/office/static/images/alarm/bell.svg') no-repeat 0 0;
  border-radius: 4px;
  &:hover {
    background-color: rgba(159, 166, 173, 0.2);
  }
  &.new {
    // 새 알람이 있는 경우
    background: url('https://static.hiworks.com/office/static/images/alarm/alarm_new.svg') no-repeat 0 0;
    &:hover {
      background-color: rgba(159, 166, 173, 0.2);
      border-radius: 4px;
    }
  }
}

.alarm-detail {
  position: absolute;
  width: 100%;
  // headerHight - ((headerHeight - iconButtonSize))/2
  max-height: calc(100vh - 70px);
  top: $headerHeight;
  right: 0;
  padding: 0;
  background: #fff;
  border: solid 1px #909090;
  border-radius: 4px;
  box-shadow: 2px 5px 4px 0 rgba(0, 0, 0, 0.16);
  overflow: auto;

  @media (min-width: 900px) {
    position: absolute;
    width: 360px;
    top: 48px;
  }

  .alarm-header {
    height: 56px;
    margin: 0 20px;
    border-bottom: 1px solid #d6d6d6;
    font-weight: 400;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .hw-icon {
      display: flex;
      padding: 7px;
      color: #909090;

      &:hover {
        border-radius: 4px;
        background-color: rgba(159, 166, 173, 0.2);
      }
      &:active {
        color: #585858;
      }
    }
  }

  .gt-clearfix {
    &::after {
      display: block;
      content: '';
      clear: both;
    }
  }
  .no-alarm {
    text-align: center;
    padding-top: 30px;
    padding-bottom: 40px;
    color: #c4c4c4;
  }
  .alarm-list {
    height: 100%;
    font-size: 14px;
    overflow: auto;
    .alarm-list-item {
      padding: 16px 20px;
      position: relative;
      color: #333;
      box-sizing: border-box;
      display: flex;
      &:hover {
        background-color: #eff4fc;
        color: currentColor;
      }
      &:after {
        position: absolute;
        left: 20px;
        right: 20px;
        bottom: 0;
        display: block;
        content: '';
        height: 1px;
        background-color: #ebebeb;
      }
      &:last-of-type {
        margin-bottom: 20px;
      }
      &.already-read {
        opacity: 0.4;
      }
      .thumb {
        width: 40px;
        height: 40px;
        img {
          width: 100%;
        }
      }
      .title {
        font-weight: bold;
        max-width: 240px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        display: block;
      }
      .desc-type-a,
      .description {
        word-break: break-all;
        line-height: 1.5;
        margin-bottom: 8px;
      }
      .date {
        font-size: 13px;
        color: #999;
      }
    }
  }
}

.alarm.is-small {
  .alarm-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    background: none;
    position: relative;
    &:hover {
      background-color: rgba(159, 166, 173, 0.2);
    }
    &.new {
      &::after {
        position: absolute;
        content: '';
        width: 5px;
        height: 5px;
        right: 3px;
        top: 3px;
        border-radius: 100%;
        background-color: #2985db;
      }
    }
  }

  .alarm-detail {
    width: 280px;
    top: $managementHeaderHeight;
    right: 20px;

    @media (min-width: 900px) {
      position: absolute;
      width: 360px;
      top: 43px;
      right: 0;
    }
  }

  .alarm-list-item {
    padding: 16px;
  }
}
</style>
